"use client";

import React from "react";
import Image from "next/image";
import { Patient } from "./types";

export interface PatientListItemTheme {
  container?: string;
  avatarWrapper?: string;
  avatar?: string;
  contentWrapper?: string;
  label?: string;
  chevron?: string;
  selected?: {
    container?: string;
    label?: string;
    chevron?: string;
  };
  hover?: {
    container?: string;
  };
}

export interface PatientListItemProps {
  patient: Patient;
  isSelected: boolean;
  onClick: () => void;
  theme?: PatientListItemTheme;
  showChevron?: boolean;
  avatarBaseUrl?: string;
  renderAvatar?: (patient: Patient, isSelected: boolean) => React.ReactNode;
  renderLabel?: (patient: Patient, isSelected: boolean) => React.ReactNode;
  renderChevron?: (isSelected: boolean) => React.ReactNode;
  className?: string;
  "data-testid"?: string;
}

const defaultTheme: PatientListItemTheme = {
  container:
    "flex flex-row items-center px-5 py-4 gap-4 cursor-pointer transition-all duration-200 rounded-xl bg-transparent border-none w-full",
  avatarWrapper: "flex-shrink-0",
  avatar: "w-12 h-12 rounded-full object-cover",
  contentWrapper: "flex-1 flex items-center",
  label: "text-lg font-medium leading-6 text-inherit",
  chevron: "w-6 h-6 text-current flex-shrink-0",
  selected: {
    container: "bg-white/5",
  },
  hover: {
    container: "hover:bg-white/[0.08]",
  },
};

export const PatientListItem: React.FC<PatientListItemProps> = ({
  patient,
  isSelected,
  onClick,
  theme = {},
  showChevron = true,
  avatarBaseUrl = "/avatars",
  renderAvatar,
  renderLabel,
  renderChevron,
  className = "",
  "data-testid": dataTestId,
}) => {
  const mergedTheme = {
    container: theme?.container || defaultTheme.container,
    avatarWrapper: theme?.avatarWrapper || defaultTheme.avatarWrapper,
    avatar: theme?.avatar || defaultTheme.avatar,
    contentWrapper: theme?.contentWrapper || defaultTheme.contentWrapper,
    label: theme?.label || defaultTheme.label,
    chevron: theme?.chevron || defaultTheme.chevron,
    selected: {
      container: theme?.selected?.container || defaultTheme.selected?.container,
      label: theme?.selected?.label || defaultTheme.selected?.label,
      chevron: theme?.selected?.chevron || defaultTheme.selected?.chevron,
    },
    hover: {
      container: theme?.hover?.container || defaultTheme.hover?.container,
    },
  };

  const containerClass = [
    mergedTheme.container,
    isSelected && mergedTheme.selected?.container,
    mergedTheme.hover?.container,
  ]
    .filter(Boolean)
    .join(" ");

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  const defaultRenderAvatar = () => {
    const imageSrc = patient.profileImage
      ? `${avatarBaseUrl}/${patient.profileImage}.svg`
      : `${avatarBaseUrl}/default.png`;

    return (
      <Image
        src={imageSrc}
        alt={`${patient.firstName} avatar`}
        width={48}
        height={48}
        className={mergedTheme.avatar}
      />
    );
  };

  const defaultRenderLabel = () => {
    const labelClass = [
      mergedTheme.label,
      isSelected && mergedTheme.selected?.label,
    ]
      .filter(Boolean)
      .join(" ");

    return <span className={labelClass}>{patient.firstName}</span>;
  };

  const defaultRenderChevron = () => {
    if (!showChevron) return null;

    const chevronClass = [
      mergedTheme.chevron,
      isSelected && mergedTheme.selected?.chevron,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={chevronClass}
      >
        <path
          d="M9 18L15 12L9 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={`${containerClass} ${className}`.trim()}
      data-testid={dataTestId || `patient-list-item-${patient.id}`}
      aria-selected={isSelected}
      aria-label={`Select ${patient.firstName}`}
    >
      <div className={mergedTheme.avatarWrapper}>
        {renderAvatar
          ? renderAvatar(patient, isSelected)
          : defaultRenderAvatar()}
      </div>

      <div className={mergedTheme.contentWrapper}>
        {renderLabel ? renderLabel(patient, isSelected) : defaultRenderLabel()}
      </div>

      {renderChevron ? renderChevron(isSelected) : defaultRenderChevron()}
    </div>
  );
};

PatientListItem.displayName = "PatientListItem";
